/* jshint esversion: 11 */
/*
 * js/api.js — centralized fetch-based API client (ES module).
 *
 * Phase 1 security-hardening keystone, now built by esbuild into
 * js/dist/api.<hash>.js as an IIFE bundle. NOT yet wired into any
 * page; the migration of the ~489 existing $.ajax callsites is
 * separate F-1 work.
 *
 * Public surface (named exports + window.api bridge):
 *   - get(path, params)             -> Promise<body>
 *   - post(path, body)              -> Promise<body>   (JSON body)
 *   - formPost(path, formData)      -> Promise<body>   (multipart)
 *
 * Behavior (preserved exactly from the Phase 1 implementation):
 *   - Resolves the API base URL via globals.returnBaseURL() / BASE_URL
 *     (single source).
 *   - Attaches Authorization: Bearer <jwt> by reading getAuth()
 *     (single source).
 *   - Rejects any non-2xx with an Error carrying .status and .body.
 *   - Times out via AbortController after API_TIMEOUT_MS (default 15s).
 *   - Does NOT call logout() or alert() — callers decide (avoids the
 *     destructive global ajaxError pattern called out by audit S-6).
 *
 * GLOBALS BRIDGE — intentional, temporary:
 *   This module depends on globals.returnBaseURL() and getAuth(),
 *   which live in the legacy non-module js/global.js. The esbuild
 *   IIFE output is loaded AFTER js/global.js on any page, so those
 *   symbols are reachable on window at runtime. We deliberately do
 *   NOT import them — js/global.js is not (yet) a module, and F-1
 *   will address that. Until then, read them off window.
 *
 *   Likewise, because no module consumer exists yet, we also assign
 *   `api` onto window so the first pages to load the bundle have a
 *   global handle. This window bridge is transitional.
 */

const API_TIMEOUT_MS = 15000;

// Reactive token refresh: dedupe concurrent 401s into a single refresh call.
// The auth/refresh route is throttled :10/min on the backend, so a burst of
// concurrent 401s must collapse into exactly ONE refresh — otherwise an
// otherwise-recoverable expiry could blow the throttle and force a logout.
let refreshInFlight = null;

function resolveBaseUrl() {
    try {
        if (window.globals && typeof window.globals.returnBaseURL === 'function') {
            const resolved = window.globals.returnBaseURL();
            if (resolved) {
                return resolved;
            }
        }
    } catch (e) {
        // fall through to BASE_URL
    }
    return window.BASE_URL || '';
}

function resolveAuthToken() {
    try {
        if (typeof window.getAuth === 'function') {
            const jwt = window.getAuth();
            if (jwt) {
                return jwt;
            }
        }
    } catch (e) {
        // fall through to localStorage
    }
    try {
        return window.localStorage ? window.localStorage.getItem('jwt') : null;
    } catch (e) {
        return null;
    }
}

function buildUrl(path, params) {
    const base = resolveBaseUrl() || '';
    let url;
    if (/^https?:\/\//i.test(path)) {
        url = path;
    } else if (base.endsWith('/') && path.startsWith('/')) {
        url = base + path.slice(1);
    } else if (!base.endsWith('/') && !path.startsWith('/')) {
        url = base + '/' + path;
    } else {
        url = base + path;
    }

    if (params && typeof params === 'object') {
        const qs = [];
        Object.keys(params).forEach(function (key) {
            const value = params[key];
            if (value === undefined || value === null) {
                return;
            }
            if (Array.isArray(value)) {
                value.forEach(function (item) {
                    if (item === undefined || item === null) {
                        return;
                    }
                    qs.push(encodeURIComponent(key + '[]') + '=' + encodeURIComponent(item));
                });
            } else {
                qs.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
            }
        });
        if (qs.length > 0) {
            url += (url.indexOf('?') === -1 ? '?' : '&') + qs.join('&');
        }
    }
    return url;
}

async function parseBody(response) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.indexOf('application/json') !== -1) {
        try {
            return await response.json();
        } catch (e) {
            return null;
        }
    }
    try {
        return await response.text();
    } catch (e) {
        return null;
    }
}

function isRefreshEndpoint(url) {
    // Belt-and-suspenders: a 401 FROM the refresh call itself must never
    // trigger another refresh. refreshInFlight already prevents recursion,
    // but the retry path in request() also gates on this so it can never
    // re-enter on the refresh endpoint.
    return typeof url === 'string' && /\/auth\/refresh(?:\?|$)/.test(url);
}

// Performs at most one in-flight refresh; concurrent callers share the
// promise. Resolves to the new token string on success, or null on failure
// (in which case request() falls through to its normal 401 throw, and the
// existing handleError -> logout path runs — which is correct for true
// session death). NEVER routed through api.post(), to avoid recursing back
// through the 401 interceptor.
function refreshAccessToken() {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        try {
            const currentJwt = resolveAuthToken();
            if (!currentJwt) return null; // nothing to refresh

            const url = buildUrl('auth/refresh');
            const resp = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + currentJwt,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'omit'
            });

            if (!resp.ok) return null; // 401/403/etc. — genuinely dead session

            const data = await resp.json().catch(() => null);
            const newToken = data && typeof data.token === 'string' ? data.token : null;
            if (!newToken) return null;

            // Reuse the existing token-storage path used by login (setAuth
            // lives in legacy js/global.js and is reachable as window.setAuth).
            if (typeof window.setAuth === 'function') {
                window.setAuth(newToken);
            } else {
                try { window.localStorage.setItem('jwt', newToken); } catch (e) { /* ignore */ }
            }
            return newToken;
        } catch (e) {
            return null; // network error / timeout — transient, do NOT log out
        } finally {
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
}

async function request(method, path, options, _isRetry) {
    options = options || {};
    const url = buildUrl(path, options.query);

    const headers = Object.assign({}, options.headers || {});
    if (!('Accept' in headers)) {
        headers['Accept'] = 'application/json';
    }
    if (!('X-Requested-With' in headers)) {
        headers['X-Requested-With'] = 'XMLHttpRequest';
    }
    const jwt = resolveAuthToken();
    if (jwt) {
        headers['Authorization'] = 'Bearer ' + jwt;
    }
    if (options.jsonBody) {
        headers['Content-Type'] = 'application/json';
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(function () { controller.abort(); }, API_TIMEOUT_MS);

    const fetchInit = {
        method: method,
        headers: headers,
        signal: controller.signal,
        credentials: 'omit'
    };
    if (options.body !== undefined) {
        fetchInit.body = options.body;
    }

    let response;
    try {
        response = await fetch(url, fetchInit);
    } catch (err) {
        clearTimeout(timeoutId);
        if (err && err.name === 'AbortError') {
            const timeoutErr = new Error('API request timed out after ' + API_TIMEOUT_MS + 'ms: ' + method + ' ' + url);
            timeoutErr.status = 0;
            timeoutErr.timeout = true;
            throw timeoutErr;
        }
        const networkErr = new Error('API network error: ' + (err && err.message ? err.message : 'unknown'));
        networkErr.status = 0;
        networkErr.cause = err;
        throw networkErr;
    }
    clearTimeout(timeoutId);

    const body = await parseBody(response);

    if (!response.ok) {
        // Reactive refresh: on a 401, attempt ONE deduplicated refresh and
        // retry this same request. Guards:
        //  - !_isRetry: never loop — a fresh token still 401'ing means
        //    the session is genuinely unusable, so fall through to throw.
        //  - !isRefreshEndpoint(url): a 401 from auth/refresh itself must
        //    never recurse.
        //  - resolveAuthToken(): nothing to refresh if we never had a token
        //    (e.g. the login call itself 401'ing on bad credentials).
        // The recursive request() call rebuilds headers from scratch, so it
        // picks up the new Bearer token written by refreshAccessToken().
        if (response.status === 401 && !_isRetry && !isRefreshEndpoint(url) && resolveAuthToken()) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                return request(method, path, options, true);
            }
            // refresh failed → fall through to the normal throw → handleError → logout
        }
        const apiErr = new Error('API ' + method + ' ' + url + ' failed: HTTP ' + response.status);
        apiErr.status = response.status;
        apiErr.body = body;
        throw apiErr;
    }
    return body;
}

export function get(path, params) {
    return request('GET', path, { query: params });
}

export function post(path, body) {
    return request('POST', path, {
        body: body === undefined ? undefined : JSON.stringify(body),
        jsonBody: true
    });
}

export function formPost(path, formData) {
    // Let the browser set the multipart Content-Type (with boundary).
    return request('POST', path, { body: formData });
}

export function postForm(path, dataObject) {
    // Sends application/x-www-form-urlencoded — the wire format the
    // Laravel backend's legacy form-data endpoints expect. ~151 jQuery
    // $.ajax POST callsites pass a plain object as `data:`, which
    // jQuery serializes as urlencoded; those callsites migrate onto
    // this, NOT onto post() (which sends JSON).
    var params = new URLSearchParams();
    if (dataObject && typeof dataObject === 'object') {
        Object.keys(dataObject).forEach(function (key) {
            var value = dataObject[key];
            if (value === undefined || value === null) {
                return;
            }
            if (Array.isArray(value)) {
                value.forEach(function (item) {
                    if (item === undefined || item === null) {
                        return;
                    }
                    params.append(key + '[]', String(item));
                });
            } else {
                params.append(key, String(value));
            }
        });
    }
    // Explicit Content-Type — some environments don't set it
    // automatically from URLSearchParams, and being explicit is safer.
    return request('POST', path, {
        body: params.toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }
    });
}

// Shared replacement for the global jQuery $(document).ajaxError handler
// (in js/global.js). Migrated callsites call this from their .catch() IN
// ADDITION TO their own per-site error handling; it only covers the
// global 401/500 net (logout / user-visible toast).
//
// The jQuery ajaxError handler stays live until F-1e — until then, this
// helper and that handler coexist; one fires for fetch-based api.* calls,
// the other for not-yet-migrated $.ajax calls.
//
// Safe to call when toast / logout aren't loaded — never throws.
export function handleError(err) {
    try {
        var status = (err && typeof err.status === 'number') ? err.status : 0;
        if (status === 401) {
            if (typeof logout === 'function') {
                logout();
            } else if (typeof window !== 'undefined' && typeof window.logout === 'function') {
                window.logout();
            }
            return;
        }
        if (status >= 500) {
            if (typeof toast !== 'undefined' && toast && typeof toast.error === 'function') {
                toast.error('There was a problem loading this page. Please try again.');
            } else if (typeof window !== 'undefined' && window.toast && typeof window.toast.error === 'function') {
                window.toast.error('There was a problem loading this page. Please try again.');
            }
            return;
        }
        // 400/404/422/etc. — callsite's own handler deals with these.
    } catch (e) {
        // never let the global error net itself throw
    }
}

export const api = { get, post, formPost, postForm, handleError };

// Transitional window bridge: until F-1 migrates consumers to import
// from this module, expose the client globally so any page that loads
// the IIFE bundle can call window.api.{get,post,formPost,postForm,handleError}.
window.api = api;
